import React, { useState, useEffect } from 'react';
import { ArrowUpRight, Copy, Check, Phone, Sparkles, Shield, Clock, Star } from 'lucide-react';
import axios from 'axios';
import { businessInfo } from '../data/mock';
import { captureAttributionFromURL, buildLeadPayload } from '../utils/attribution';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [couponData, setCouponData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    captureAttributionFromURL();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    setIsLoading(true);
    try {
      const payload = buildLeadPayload(
        {
          email,
          name: name || undefined,
          phone: phone || undefined,
        },
        { source: 'landing_offer_page' }
      );
      const response = await axios.post(`${API}/leads`, payload);
      setCouponData(response.data);
    } catch (err) {
      setCouponData({ couponCode: 'PRIVE15', discount: '15%' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(couponData?.couponCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-onyx text-ivory">
      {/* Header */}
      <header className="px-5 sm:px-8 lg:px-12 py-6 border-b border-pewter/60">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Snatched Beauties Privé" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <p className="font-serif text-ivory text-base">
                Snatched Beauties <span className="brand-italic chrome-rose">Privé</span>
              </p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-silverx mt-0.5">
                by {businessInfo.legalName}
              </p>
            </div>
          </div>
          <a
            href={`tel:${businessInfo.phone}`}
            className="flex items-center gap-2 text-platinum hover:text-blush transition-colors text-[12px] tracking-[0.2em] uppercase"
          >
            <Phone size={13} /> {businessInfo.phone}
          </a>
        </div>
      </header>

      <main className="px-5 sm:px-8 lg:px-12 py-16 md:py-24 grain relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-[0.15]"
          style={{ background: 'radial-gradient(circle, #D1679A 0%, transparent 60%)' }}
        />
        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          {/* Value */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Sparkles size={14} className="text-blush" />
              <span className="eyebrow">A Private Invitation</span>
            </div>
            <h1 className="font-serif text-ivory text-4xl sm:text-5xl lg:text-6xl leading-[1.04] mb-6">
              Your first Privé ritual,
              <br />
              <span className="brand-italic chrome-rose">15% elevated.</span>
            </h1>
            <p className="text-platinum text-base md:text-lg leading-relaxed mb-10 max-w-xl">
              Curated non-invasive treatments designed to support confidence, contour, recovery,
              and whole-body wellness—delivered through an elevated boutique experience.
            </p>

            <div className="grid sm:grid-cols-3 gap-6 mb-12">
              {[
                { Icon: Shield, label: 'Licensed', sub: '& Insured' },
                { Icon: Clock, label: 'Same-Day', sub: 'Availability' },
                { Icon: Star, label: '5.0 Rated', sub: 'Private Clients' },
              ].map(({ Icon, label, sub }) => (
                <div key={label} className="flex items-start gap-3">
                  <div className="w-10 h-10 border border-pewter flex items-center justify-center flex-shrink-0">
                    <Icon size={14} className="text-blush" />
                  </div>
                  <div>
                    <p className="text-ivory text-sm">{label}</p>
                    <p className="text-silverx text-[11px] tracking-[0.18em] uppercase mt-1">{sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="font-serif brand-italic text-silverx text-sm max-w-md">
              Curated treatments. Elevated experience. Visible results.
            </p>
          </div>

          {/* Form / Success */}
          <div className="bg-carbon border border-pewter/70 p-8 md:p-10">
            {!couponData ? (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <p className="eyebrow mb-3">Claim your code</p>
                  <h2 className="font-serif text-ivory text-2xl md:text-3xl leading-tight">
                    Reserve <span className="brand-italic chrome-rose">15% off</span>.
                  </h2>
                </div>

                <div>
                  <label className="eyebrow block mb-3">Name (Optional)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-2.5 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@example.com"
                    required
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-2.5 outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Phone (Optional)</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(323) 555 0123"
                    className="w-full bg-transparent border-b border-pewter focus:border-blush text-ivory placeholder-silverx/60 py-2.5 outline-none transition-colors"
                  />
                </div>

                {error && <p className="text-red-300 text-xs">{error}</p>}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-pill w-full inline-flex justify-center items-center gap-3 px-6 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush disabled:opacity-50 mt-2"
                >
                  {isLoading ? 'Sending…' : 'Claim My Code'}
                  <ArrowUpRight size={14} />
                </button>

                <p className="text-silverx/70 text-[11px] tracking-wider uppercase text-center">
                  No spam—just quiet, curated wellness.
                </p>
              </form>
            ) : (
              <div className="text-center">
                <p className="eyebrow mb-4">Welcome to Privé</p>
                <h2 className="font-serif text-ivory text-3xl mb-6">You're on the list.</h2>

                <div className="border border-dashed border-blush/70 p-6 mb-6">
                  <p className="eyebrow mb-3 text-blush">Your Code</p>
                  <div className="flex items-center justify-center gap-3">
                    <span className="font-serif text-ivory text-2xl md:text-3xl tracking-wider">
                      {couponData.couponCode}
                    </span>
                    <button
                      onClick={handleCopy}
                      className="p-2 text-silverx hover:text-blush transition-colors"
                      aria-label="Copy"
                    >
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                  <p className="font-serif brand-italic chrome-rose text-lg mt-4">
                    15% off your first treatment
                  </p>
                </div>

                <a
                  href={businessInfo.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pill w-full inline-flex justify-center items-center gap-3 px-6 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush"
                >
                  Book Your Consultation
                  <ArrowUpRight size={14} />
                </a>
                <p className="text-silverx/70 text-[11px] tracking-wider uppercase mt-4">
                  Valid for 30 days.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="border-t border-pewter/60 px-5 sm:px-8 lg:px-12 py-10 text-center">
        <p className="text-silverx text-[11px] tracking-[0.2em] uppercase">
          © {new Date().getFullYear()} {businessInfo.legalName} · {businessInfo.tagline}
        </p>
      </footer>
    </div>
  );
};

export default LandingPage;
